import type {
  I$W,
  IWixWindow,
  PlatformControllerFlowAPI,
  TFunction,
  ISiteApis,
} from '@wix/yoshi-flow-editor';
import type {
  DispatchStateByLocation,
  Operation,
  DispatchType,
  DispatchLocation,
} from 'root/types/businessTypes';
import { SchedulingType } from 'root/types/businessTypes';
import { DISPATCH_MODAL_WIDGET_COMPONENT_IDS } from '../../appConsts/blocksIds';
import { autorun, toJS, reaction } from 'mobx';
import { LiveSiteClicksInsideDispatchModalButtonType } from '@wix/restaurants-bi';
import type { DispatchInfo, AddressInputAddress } from '../../types/businessTypes';
import type { IBIReporterService } from 'root/services/biReporterService';
import { DispatchModalForm } from './dispatchModalForm';
import {
  convertAddressInputToAddress,
  type DispatchModalStore,
} from 'root/states/DispatchModalStore';
import { BIEventsHandler, ErrorType, isValidDate } from './dispatchModalUtils';
import { dispatchState } from 'root/states/DispatchState';
import {
  BUTTON_STATES,
  getASAPString,
  NOTIFICATION_BACKGROUND_COLORS,
  STATUS_INDICATOR_COLORS,
} from '../Header/headerUtils';
import type { BindAll, IDispatchModalController } from './types';
import { DEFAULT_TIMEZONE } from 'root/api/consts';
import { getDateFormat } from 'root/utils/dateTimeUtils';
import { fulfillmentPickerStore } from 'root/states/FulfillmentPickerStore';
import { getSiteLocale } from 'root/utils/siteDataUtils';
import type { CommonAddress } from '@wix/ambassador-wix-atlas-service-web/types';
import { withBIDuration } from 'root/utils/withBIDuration';
import { itemState } from 'root/states/itemsState';
import { menusState } from 'root/states/MenusState';
import { getOperationBIEventSettings } from 'root/utils/BIDurationUtils';

export class DispatchModalController implements IDispatchModalController {
  private form!: DispatchModalForm;
  constructor(
    private $bindAll: BindAll,
    private $w: I$W,
    private flowAPI: PlatformControllerFlowAPI,
    private window: IWixWindow,
    private isEditor: boolean,
    private store: DispatchModalStore,
    private operation: Operation,
    private onSave: ({
      dispatchType,
      dispatchInfo,
      operationId,
      isLocationChanged,
    }: {
      dispatchType: DispatchType;
      dispatchInfo: DispatchInfo;
      operationId: string;
      isLocationChanged: boolean;
    }) => Promise<void>,
    private biReporterService?: IBIReporterService,
    private isViewer: boolean = true,
    private locationChanged?: (address?: CommonAddress) => Promise<void>
  ) {
    const {
      flowAPI: {
        translations,

        controllerConfig: {
          wixCodeApi: { site },
        },

        panoramaClient,
        reportError,
      },
    } = this;
    const t = translations.t as TFunction;
    const timezone = dispatchState.timezone || site.timezone || DEFAULT_TIMEZONE;
    const locale = getSiteLocale(this.flowAPI);
    this.form = new DispatchModalForm(
      store,
      t,
      timezone,
      locale,
      panoramaClient?.errorMonitor(),
      reportError
    );
  }

  formInit() {
    this.form.init();
  }

  setPickupFocus() {
    if (
      this.store.multiLocation &&
      this.store.multiLocationForCurrentDispatchType &&
      !this.store.showPickupLocationSelector
    ) {
      this.$w(DISPATCH_MODAL_WIDGET_COMPONENT_IDS.changeLocation).focus();
      return true;
    } else if (this.store.showPickupLocationSelector) {
      this.$w(DISPATCH_MODAL_WIDGET_COMPONENT_IDS.pickupLocationPicker).focus();
    }
  }

  setDeliveryFocus() {
    if (!this.form.address) {
      this.$w(DISPATCH_MODAL_WIDGET_COMPONENT_IDS.addressInput).focus();
      return true;
    }
  }

  setFocus() {
    setTimeout(() => {
      const isFocused = this.form.isPickup ? this.setPickupFocus() : this.setDeliveryFocus();
      if (!isFocused) {
        if (this.form.isScheduleForLater) {
          this.$w(DISPATCH_MODAL_WIDGET_COMPONENT_IDS.dateSlots).focus();
        } else if (this.form.isPreOrder) {
          this.$w(DISPATCH_MODAL_WIDGET_COMPONENT_IDS.timeSlots).focus();
        } else if (this.form.isASAP && !this.form.isSchedulingPickerDisplayed) {
          this.$w(DISPATCH_MODAL_WIDGET_COMPONENT_IDS.saveButton).focus();
        }
      }
    }, 500);
  }

  init(
    onModalOpen: () => void,
    closeModal: (
      window: IWixWindow,
      data?: { dispatchType: DispatchType; dispatchInfo: DispatchInfo }
    ) => void,
    initialDispatchState?: DispatchStateByLocation
  ) {
    const t = this.flowAPI.translations.t as TFunction;
    const site = this.flowAPI.controllerConfig.wixCodeApi.site as ISiteApis;
    const isMemberLoggedIn = !!this.flowAPI.controllerConfig.wixCodeApi.user.currentUser.loggedIn;

    const biHandler = new BIEventsHandler(
      this.form,
      dispatchState.configuredDispatchTypes,
      this.operation,
      isMemberLoggedIn,
      this.store.schedulingType,
      this.biReporterService,
      initialDispatchState
    );

    const timezone = dispatchState.timezone || site.timezone || DEFAULT_TIMEZONE;
    const locale = getSiteLocale(this.flowAPI);
    this.form.init();

    autorun(() => {
      const multiStateBox = this.$w(DISPATCH_MODAL_WIDGET_COMPONENT_IDS.dispatchStateBox);
      multiStateBox.changeState(this.form.dispatchType.toLowerCase());
      this.setFocus();
    });

    this.flowAPI.panoramaClient?.errorMonitor()?.addBreadcrumb({
      category: 'DispatchModalController',
      message: 'init',
      data: { store: this.store },
    });

    const onDispatchTypeChange = async (dispatchType: DispatchType) => {
      biHandler.onDispatchTypeChange(dispatchType);
      await this.form.setDispatchType(dispatchType);
    };

    autorun(() => {
      fulfillmentPickerStore.setObservableProps({
        selectedDispatchType: this.form.dispatchType,
        onDispatchTypeChange,
        hasPopup: false,
        shouldConsiderAllLocations: true,
      });
    });

    reaction(
      () => !this.store.isLoading,
      () => this.changeSchedulingTypeModalState()
    );

    const deliveryLocationChangedWithDuration = async (locationId: string) => {
      await withBIDuration({
        callback: () => deliveryLocationChanged(locationId),
        name: 'deliveryLocationChanged',
        ...getOperationBIEventSettings(menusState, itemState, this.biReporterService),
      });
    };

    const deliveryLocationChanged = async (locationId: string) => {
      const newTimezone =
        dispatchState.timezone ||
        this.flowAPI.controllerConfig.wixCodeApi.site.timezone ||
        DEFAULT_TIMEZONE;
      this.form.setTimezone(newTimezone);

      this.store.setIsChangingLocation(true);
      this.store.setLocationId(locationId);
      const address = this.form.address
        ? convertAddressInputToAddress(this.form.address)
        : dispatchState.dispatchInfo.address;
      await this.locationChanged?.(address);

      await this.form.changeLocation();
      this.changeSchedulingTypeModalState();
      this.store.setIsChangingLocation(false);
    };

    const pickupLocationChangedWithDuration = async () => {
      await withBIDuration({
        callback: () => pickupLocationChanged(),
        name: 'pickupLocationChanged',
        ...getOperationBIEventSettings(menusState, itemState, this.biReporterService),
      });
    };

    const pickupLocationChanged = async () => {
      // Update timezone for the new location
      const newTimezone =
        dispatchState.timezone ||
        this.flowAPI.controllerConfig.wixCodeApi.site.timezone ||
        DEFAULT_TIMEZONE;
      this.form.setTimezone(newTimezone);
      await this.locationChanged?.();

      await this.form.changeLocation();
      this.changeSchedulingTypeModalState();
    };

    const onSaveWithDuration = async ({
      dispatchType,
      dispatchInfo,
      operationId,
      isLocationChanged,
    }: {
      dispatchType: DispatchType;
      dispatchInfo: DispatchInfo;
      operationId: string;
      isLocationChanged: boolean;
    }) => {
      await withBIDuration({
        callback: () => onSave({ dispatchType, dispatchInfo, operationId, isLocationChanged }),
        name: 'onDispatchSave',
        ...getOperationBIEventSettings(menusState, itemState, this.biReporterService),
      });
    };

    const onSave = async ({
      dispatchType,
      dispatchInfo,
      operationId,
      isLocationChanged,
    }: {
      dispatchType: DispatchType;
      dispatchInfo: DispatchInfo;
      operationId: string;
      isLocationChanged: boolean;
    }) => {
      const errorMonitor = this.flowAPI.panoramaClient?.errorMonitor();

      try {
        // Add breadcrumb for onSave start
        errorMonitor?.addBreadcrumb({
          category: 'DispatchModalController',
          message: 'onSave started',
          data: {
            dispatchType,
            operationId,
            isLocationChanged,
            dispatchInfo: {
              selectedTimeSlot: dispatchInfo.selectedTimeSlot,
              address: dispatchInfo.address
                ? {
                    formatted: dispatchInfo.address.formattedAddress,
                    country: dispatchInfo.address.country,
                    city: dispatchInfo.address.city,
                    postalCode: dispatchInfo.address.postalCode,
                    streetAddress: dispatchInfo.address.streetAddress,
                  }
                : null,
            },
            storeState: {
              isLoading: this.store.isLoading,
              isChangingLocation: this.store.isChangingLocation,
              multiLocation: this.store.multiLocation,
              locationId: this.store.locationId,
              schedulingType: this.store.schedulingType,
              hasError: this.form.hasError,
            },
            formState: {
              dispatchType: this.form.dispatchType,
              schedulingType: this.form.schedulingType,
              isASAP: this.form.isASAP,
              isPreOrder: this.form.isPreOrder,
              isScheduleForLater: this.form.isScheduleForLater,
              selectedOption: this.form.selectedOption
                ? {
                    value: this.form.selectedOption.value,
                    label: this.form.selectedOption.label,
                  }
                : null,
              selectedDate: this.form.selectedDate?.toISOString(),
              hasAddress: !!this.form.address,
              errorText: this.form.errorText,
            },
            operation: {
              id: this.operation.id,
              locationId: this.operation.locationId,
              operationGroupId: this.operation.operationGroupId,
              isDefaultLocation: this.operation.locationDetails?.default,
            },
          },
        });

        // Add breadcrumb for validation check
        const canSubmit = await this.form.canSubmit();
        errorMonitor?.addBreadcrumb({
          category: 'DispatchModalController',
          message: 'onSave validation check',
          data: {
            canSubmit,
            formErrors: this.form.hasError ? this.form.errorText : null,
            addressValidation: {
              hasAddress: !!this.form.address,
              addressFormatted: this.form.address?.formatted,
            },
          },
        });

        if (!canSubmit) {
          errorMonitor?.addBreadcrumb({
            category: 'DispatchModalController',
            message: 'onSave validation failed - early return',
            data: {
              hasError: this.form.hasError,
              errorText: this.form.errorText,
              hasAddress: !!this.form.address,
              isLoading: this.store.isLoading,
              isChangingLocation: this.store.isChangingLocation,
            },
          });
          return;
        }

        // Add breadcrumb before calling this.onSave
        errorMonitor?.addBreadcrumb({
          category: 'DispatchModalController',
          message: 'onSave calling parent onSave handler',
          data: {
            dispatchType,
            operationId,
            isLocationChanged,
            timestamp: new Date().toISOString(),
          },
        });

        await this.onSave({
          dispatchType,
          dispatchInfo,
          operationId,
          isLocationChanged,
        });

        // Add breadcrumb for successful completion
        errorMonitor?.addBreadcrumb({
          category: 'DispatchModalController',
          message: 'onSave completed successfully',
          data: {
            dispatchType,
            operationId,
            isLocationChanged,
            completedAt: new Date().toISOString(),
          },
        });
      } catch (error) {
        // Log error with extensive context
        errorMonitor?.addBreadcrumb({
          category: 'DispatchModalController',
          message: 'onSave error occurred',
          data: {
            error:
              error instanceof Error
                ? {
                    message: error.message,
                    stack: error.stack,
                    name: error.name,
                  }
                : error,
            context: {
              dispatchType,
              operationId,
              isLocationChanged,
              dispatchInfo: {
                selectedTimeSlot: dispatchInfo.selectedTimeSlot,
                hasAddress: !!dispatchInfo.address,
              },
              storeState: {
                isLoading: this.store.isLoading,
                isChangingLocation: this.store.isChangingLocation,
                multiLocation: this.store.multiLocation,
                locationId: this.store.locationId,
              },
              formState: {
                hasError: this.form.hasError,
                errorText: this.form.errorText,
                dispatchType: this.form.dispatchType,
              },
            },
            timestamp: new Date().toISOString(),
          },
        });

        // Report the error to Panorama
        errorMonitor?.reportError(error as Error);

        // Also report to flowAPI for consistency
        this.flowAPI.reportError(error as Error);

        // Re-throw to maintain existing error handling behavior
        throw error;
      }
    };

    try {
      this.changeSchedulingTypeModalState();
      this.$bindAll({
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.multipleDelivery]: {
          deleted: () => this.isEditor && !this.store.hasMultipleDeliveryOperationsForAddress,
          collapsed: () =>
            !this.store.hasMultipleDeliveryOperationsForAddress ||
            this.store.isLoading ||
            this.form.hasError,
          hidden: () => false,
          options: () => this.form.deliveryAvailableLocations,
          value: () => this.form.deliverySelectedAvailableLocation,
          onChange: async (event: $w.Event) => {
            deliveryLocationChangedWithDuration(event.target.value);
          },
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.changeLocationWrapper]: {
          deleted: () =>
            this.isEditor &&
            (!this.store.multiLocation ||
              !this.store.multiLocationForCurrentDispatchType ||
              this.store.showPickupLocationSelector),
          collapsed: () =>
            !this.store.multiLocation ||
            !this.store.multiLocationForCurrentDispatchType ||
            this.store.showPickupLocationSelector,
          hidden: () => false,
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.changeLocation]: {
          onClick: () => {
            biHandler.onLocationChangeClick();
            this.store.setIsLocationSelected(false);
            setTimeout(() => {
              this.$w(DISPATCH_MODAL_WIDGET_COMPONENT_IDS.pickupLocationPicker).focus();
            }, 200);
          },
          label: () => t('menu_olo.dispatchModal.changeLocation'),
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.buttonStateBox]: {
          currentState: () =>
            this.store.showPickupLocationSelector ? BUTTON_STATES.NEXT : BUTTON_STATES.SAVE,
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.nextButton]: {
          onClick: async () => {
            pickupLocationChangedWithDuration();
            setTimeout(() => {
              this.$w(DISPATCH_MODAL_WIDGET_COMPONENT_IDS.changeLocation).focus();
            }, 200);
          },
          disabled: () => !this.store.locationId,
          label: () => t('menu_olo.dispatchModal.nextButton'),
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.locationNamePickupDispatch]: {
          deleted: () =>
            this.isEditor && (!this.store.multiLocation || this.store.showPickupLocationSelector),
          collapsed: () => !this.store.multiLocation || this.store.showPickupLocationSelector,
          hidden: () => false,
          text: () => this.store.pickupLocationName,
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.locationLobby]: {
          deleted: () => this.isEditor && !this.store.showPickupLocationSelector,
          collapsed: () => !this.store.showPickupLocationSelector,
          hidden: () => false,
          data: () => this.store.pickupLocations,
          selectedIndex: () => this.store.getSelectedLocationIndex,
          item: (itemData: DispatchLocation, bindItem: Function, index: number) => {
            bindItem(DISPATCH_MODAL_WIDGET_COMPONENT_IDS.pickupLocationName, {
              text: () => itemData.name,
              collapsed: () => false,
            });
            bindItem(DISPATCH_MODAL_WIDGET_COMPONENT_IDS.pickupLocationAddress, {
              text: () => itemData.address?.formattedAddress,
            });
            bindItem(DISPATCH_MODAL_WIDGET_COMPONENT_IDS.acceptOrdersText, {
              text: () => {
                return itemData.acceptOrders
                  ? t('menu_olo.dispatchModal.pickup.status.AcceptingOrders')
                  : t('menu_olo.dispatchModal.pickup.status.NotAcceptingOrders');
              },
            });
            bindItem(DISPATCH_MODAL_WIDGET_COMPONENT_IDS.statusIndicator, {
              style: {
                backgroundColor: () =>
                  itemData.acceptOrders
                    ? STATUS_INDICATOR_COLORS.ONLINE
                    : STATUS_INDICATOR_COLORS.OFFLINE,
              },
            });
            bindItem(DISPATCH_MODAL_WIDGET_COMPONENT_IDS.pickupLocationPicker, {
              inputLabel: () => itemData.name,
              onSelect: (event: $w.Event) => {
                this.store.setLocationId(itemData._id, index);
                biHandler.onLocationPickerClick();
              },
            });
          },
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.NotAcceptingError]: {
          style: {
            backgroundColor: () =>
              !dispatchState.isSelectedDispatchAvailable()
                ? NOTIFICATION_BACKGROUND_COLORS.ERROR
                : NOTIFICATION_BACKGROUND_COLORS.INFO,
          },
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.NotAcceptingErrorText]: {
          text: () =>
            !dispatchState.isSelectedDispatchAvailable()
              ? t('menu_olo.dispatchModal.error.notAcceptOrders')
              : t('menu_olo.dispatchModal.info.emptyCart'),
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.deliverFromSubtitle]: {
          deleted: () =>
            this.isEditor &&
            (this.form.hasError ||
              !this.store.isMultiLocation ||
              !this.form.deliverySelectedAvailableLocationName),
          collapsed: () =>
            this.form.hasError ||
            !this.store.isMultiLocation ||
            !this.form.deliverySelectedAvailableLocationName ||
            this.store.isLoading,
          hidden: () => false,
          text: () => t('menu_olo.dispatchModal.delivery.from'),
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.deliveryFromAddress]: {
          deleted: () =>
            this.isEditor &&
            (!this.store.multiLocation || this.store.hasMultipleDeliveryOperationsForAddress),
          collapsed: () =>
            !this.store.multiLocation || this.store.hasMultipleDeliveryOperationsForAddress,
          hidden: () => false,
          text: () => this.form.deliverySelectedAvailableLocationName,
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.safetyBoxForChangeLocation]: {
          deleted: () => this.isEditor,
          hidden: () => false,
          collapsed: () =>
            !this.form.address ||
            this.store.isLoading ||
            this.form.hasError ||
            (dispatchState.isSelectedDispatchAvailable() &&
              !this.store.isLocationChanged &&
              this.store.isCurrentLocationAvailableForAddress),
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.safetyBoxForChangeLocationContainer]: {
          style: {
            backgroundColor: () =>
              !dispatchState.isSelectedDispatchAvailable()
                ? NOTIFICATION_BACKGROUND_COLORS.ERROR
                : NOTIFICATION_BACKGROUND_COLORS.INFO,
          },
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.safetyBoxForChangeLocationText]: {
          text: () =>
            !dispatchState.isSelectedDispatchAvailable()
              ? t('menu_olo.dispatchModal.delivery.unavailableLocation')
              : t('menu_olo.dispatchModal.info.emptyCart'),
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.safetyBoxForError]: {
          deleted: () =>
            this.isEditor &&
            (this.store.showPickupLocationSelector ||
              (dispatchState.isSelectedDispatchAvailable() && !this.store.isLocationChanged)),
          collapsed: () =>
            this.store.isLoading ||
            this.store.showPickupLocationSelector ||
            (dispatchState.isSelectedDispatchAvailable() && !this.store.isLocationChanged),

          hidden: () => false,
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.timeSlots]: {
          disabled: () => this.store.isChangingLocation,
          collapsed: () =>
            (this.form.hasError ||
              !this.form.isScheduleForLater ||
              this.form.dropdownTimeSlotsOptions.length === 0 ||
              this.store.isLoading) &&
            !this.store.isChangingLocation,
          value: () => {
            if (!this.isViewer) {
              setTimeout(
                () =>
                  this.$w(DISPATCH_MODAL_WIDGET_COMPONENT_IDS.timeSlots).resetValidityIndication(),
                0
              );
            }
            return this.form.selectedOption?.value;
          },
          options: () => this.form.dropdownTimeSlotsOptions,
          onChange: (event: $w.Event) => {
            biHandler.onTimeSlotChange();
            this.form.setSelectedOption(event.target.value);
            this.$w(DISPATCH_MODAL_WIDGET_COMPONENT_IDS.saveButton).focus();
          },
          onClick: biHandler.onTimeSlotClick,
          label: () => t('menu_olo.dispatchModal.timePicker'),
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.dateSlots]: {
          disabled: () => this.store.isChangingLocation,
          collapsed: () =>
            (this.form.shouldCollapseDateSlotDropdown || this.store.isLoading) &&
            !this.store.isChangingLocation,
          value: () => {
            if (!this.isViewer) {
              setTimeout(
                () =>
                  this.$w(DISPATCH_MODAL_WIDGET_COMPONENT_IDS.dateSlots).resetValidityIndication(),
                0
              );
            }
            return this.form.selectedDate?.toDateString();
          },
          options: () => this.form.dropdownDatesOptions,
          onChange: (event: $w.Event) => {
            biHandler.onDateSlotChange();
            this.form.setSelectedDate(new Date(event.target.value));
          },
          onClick: biHandler.onDateSlotClick,
          label: () => t('menu_olo.dispatchModal.datePicker'),
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.saveButton]: {
          onClick: async () => {
            const errorMonitor = this.flowAPI.panoramaClient?.errorMonitor();

            try {
              const { selectedDispatchType: dispatchType, dispatchInfo } = dispatchState;

              // Log save button click start
              errorMonitor?.addBreadcrumb({
                category: 'DispatchModalController',
                message: 'saveButton onClick started',
                data: {
                  dispatchType,
                  dispatchInfo: {
                    selectedTimeSlot: dispatchInfo.selectedTimeSlot,
                    hasAddress: !!dispatchInfo.address,
                  },
                  storeState: {
                    multiLocation: this.store.multiLocation,
                    locationId: this.store.locationId,
                    isLocationChanged: this.store.isLocationChanged,
                    isLoading: this.store.isLoading,
                    isChangingLocation: this.store.isChangingLocation,
                  },
                  formState: {
                    hasError: this.form.hasError,
                    errorText: this.form.errorText,
                    hasAddress: !!this.form.address,
                    dispatchType: this.form.dispatchType,
                  },
                  operationState: {
                    currentOperationId: dispatchState.operationId,
                    selectedLocationOperation: this.store.selectedLocationOperation?.id,
                  },
                },
              });

              biHandler.onSaveButtonClick(dispatchInfo.selectedTimeSlot);

              // Log before validation
              errorMonitor?.addBreadcrumb({
                category: 'DispatchModalController',
                message: 'saveButton onClick - before canSubmit validation',
                data: {
                  formHasError: this.form.hasError,
                  formErrorText: this.form.errorText,
                  hasAddress: !!this.form.address,
                  isLoading: this.store.isLoading,
                },
              });

              const canSubmit = await this.form.canSubmit();

              // Log validation result
              errorMonitor?.addBreadcrumb({
                category: 'DispatchModalController',
                message: 'saveButton onClick - canSubmit validation result',
                data: {
                  canSubmit,
                  formHasError: this.form.hasError,
                  formErrorText: this.form.errorText,
                  hasAddress: !!this.form.address,
                },
              });

              if (canSubmit) {
                const operationId = this.store.multiLocation
                  ? this.store.selectedLocationOperation?.id
                  : dispatchState.operationId;

                // Log operation ID resolution
                errorMonitor?.addBreadcrumb({
                  category: 'DispatchModalController',
                  message: 'saveButton onClick - operation ID resolved',
                  data: {
                    operationId,
                    multiLocation: this.store.multiLocation,
                    selectedLocationOperationId: this.store.selectedLocationOperation?.id,
                    dispatchStateOperationId: dispatchState.operationId,
                  },
                });

                if (this.store.multiLocation && this.store.locationId) {
                  const { locationId } =
                    this.flowAPI.controllerConfig.wixCodeApi.location.query || {};

                  // Log location handling
                  errorMonitor?.addBreadcrumb({
                    category: 'DispatchModalController',
                    message: 'saveButton onClick - handling location query params',
                    data: {
                      storeLocationId: this.store.locationId,
                      queryLocationId: locationId,
                      willUpdateQueryParams: this.store.locationId !== locationId,
                    },
                  });

                  if (this.store.locationId !== locationId) {
                    this.flowAPI.controllerConfig.wixCodeApi.location.queryParams.add({
                      locationId: this.store.locationId,
                    });

                    errorMonitor?.addBreadcrumb({
                      category: 'DispatchModalController',
                      message: 'saveButton onClick - updated location query params',
                      data: {
                        newLocationId: this.store.locationId,
                      },
                    });
                  }
                }

                // Log before state updates
                errorMonitor?.addBreadcrumb({
                  category: 'DispatchModalController',
                  message: 'saveButton onClick - before state updates',
                  data: {
                    operationId,
                    willSetCurrentOperationId: !!operationId,
                    isLocationChanged: !!this.store.isLocationChanged,
                  },
                });

                operationId && dispatchState.setCurrentOperationId(operationId);
                dispatchState.setIsLocationSelected(true);

                // Log before onSave call
                errorMonitor?.addBreadcrumb({
                  category: 'DispatchModalController',
                  message: 'saveButton onClick - before onSaveWithDuration call',
                  data: {
                    operationId,
                    dispatchType,
                    isLocationChanged: !!this.store.isLocationChanged,
                    willCallOnSave: !!operationId,
                  },
                });

                operationId &&
                  (await onSaveWithDuration({
                    dispatchType,
                    dispatchInfo,
                    operationId,
                    isLocationChanged: !!this.store.isLocationChanged,
                  }));

                // Log before modal close
                errorMonitor?.addBreadcrumb({
                  category: 'DispatchModalController',
                  message: 'saveButton onClick - before closing modal',
                  data: {
                    dispatchType,
                    hasDispatchInfo: !!dispatchInfo,
                  },
                });

                closeModal(this.window, { dispatchType, dispatchInfo });

                // Log successful completion
                errorMonitor?.addBreadcrumb({
                  category: 'DispatchModalController',
                  message: 'saveButton onClick completed successfully',
                  data: {
                    dispatchType,
                    operationId,
                    isLocationChanged: !!this.store.isLocationChanged,
                    completedAt: new Date().toISOString(),
                  },
                });
              } else {
                // Log when canSubmit is false
                errorMonitor?.addBreadcrumb({
                  category: 'DispatchModalController',
                  message: 'saveButton onClick - canSubmit validation failed',
                  data: {
                    formHasError: this.form.hasError,
                    formErrorText: this.form.errorText,
                    hasAddress: !!this.form.address,
                    isLoading: this.store.isLoading,
                    isChangingLocation: this.store.isChangingLocation,
                  },
                });
              }
            } catch (error) {
              // Log error with extensive context
              errorMonitor?.addBreadcrumb({
                category: 'DispatchModalController',
                message: 'saveButton onClick error occurred',
                data: {
                  error:
                    error instanceof Error
                      ? {
                          message: error.message,
                          stack: error.stack,
                          name: error.name,
                        }
                      : error,
                  context: {
                    dispatchState: {
                      selectedDispatchType: dispatchState.selectedDispatchType,
                      operationId: dispatchState.operationId,
                    },
                    storeState: {
                      multiLocation: this.store.multiLocation,
                      locationId: this.store.locationId,
                      isLocationChanged: this.store.isLocationChanged,
                      isLoading: this.store.isLoading,
                      isChangingLocation: this.store.isChangingLocation,
                    },
                    formState: {
                      hasError: this.form.hasError,
                      errorText: this.form.errorText,
                      hasAddress: !!this.form.address,
                      dispatchType: this.form.dispatchType,
                    },
                  },
                  timestamp: new Date().toISOString(),
                },
              });

              // Report the error to Panorama
              errorMonitor?.reportError(error as Error);

              // Also report to flowAPI for consistency
              this.flowAPI.reportError(error as Error);

              // Re-throw to maintain existing error handling behavior
              throw error;
            }
          },
          disabled: () =>
            this.store.isLoading ||
            !this.form.address ||
            this.form.hasError ||
            this.store.isChangingLocation,
          label: () => t('menu_olo.dispatchModal.saveButton'),
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.addressInput]: {
          onChange: async (evt: $w.Event) => {
            await this.form.setAddress(evt.target.value as AddressInputAddress);
            if (this.form.hasError && evt.target.value.country) {
              this.form.errorText === ErrorType.ADDRESS_NOT_FOUND.toString()
                ? biHandler.onAddressNotFound()
                : biHandler.onNoAvailableFulfillments();
            }
          },
          onClick: biHandler.onAddressInputClick,
          value: () => toJS(this.form.address),
          label: () => t('menu_olo.dispatchModal.delivery.to'),
          placeholder: () => t('menu_olo.dispatchModal.delivery.addressInput.placeholder'),
          // NOTE: this is not supported yet
          accessibility: {
            ariaAttributes: {
              describedBy: () => [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.errorContainer],
            },
          },
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.asapText]: {
          text: () => getASAPString(this.store.asapTimeExact, this.store.asapTimeRange, t),
          collapsed: () =>
            this.form.hasError || !(this.store.asapTimeExact || this.store.asapTimeRange),
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.closeButton]: {
          onClick: () => {
            biHandler.onCloseButtonClick(dispatchState.dispatchInfo.selectedTimeSlot);
            closeModal(this.window);
          },
          accessibility: {
            ariaAttributes: {
              label: () => t('menu_olo.dispatchModal.closeButton.label'),
            },
          },
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.errorMessage]: {
          text: () => this.form.errorText || '',
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.errorContainer]: {
          accessibility: {
            role: () => 'alert',
          },
          collapsed: () =>
            ((this.store.isLoading || this.store.isChangingLocation) && !this.store.address) ||
            !this.form.hasError ||
            this.form.errorText === ErrorType.ADDRESS_NOT_FOUND.toString(),
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.datePickerContainer]: {
          collapsed: () =>
            !this.form.address ||
            this.form.hasError ||
            (this.form.dropdownTimeSlotsOptions.length === 0 &&
              !(this.store.isLoading || this.store.isChangingLocation)),
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.datePicker]: {
          collapsed: () => this.store.isLoading && !this.store.isChangingLocation,
          disabled: () => this.store.isChangingLocation,
          onChange: async (evt: $w.Event) => {
            await this.form.setSelectedDate(evt.target.value);
          },
          onViewChange: async (evt: $w.Event) => {
            // @ts-expect-error
            const { startDate: from, endDate: until } = evt.options ?? {};
            if (isValidDate(timezone, until)) {
              await this.store.setAvailableDates({ from, until });
            }
          },
          onClick: biHandler.onDatePickerClick,
          dateFormat: () => getDateFormat(locale),
          value: () => this.form.selectedDate,
          label: () => t('menu_olo.dispatchModal.datePicker'),
          enabledDateRanges: () => [
            ...this.store.availableDateRanges,
            ...(this.form.selectedDate
              ? [{ startDate: this.form.selectedDate, endDate: this.form.selectedDate }]
              : []),
          ],
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.dateTimeSlots]: {
          options: () => this.form.dropdownTimeSlotsOptions,
          onChange: (evt: $w.Event) => this.form.setSelectedOption(evt.target.value),
          onClick: () =>
            this.biReporterService?.reportOloLiveSiteClicksInsideDispatchModalBiEvent({
              dispatchType: this.form.dispatchType,
              isPreorder: this.form.isPreOrder,
              buttonType: LiveSiteClicksInsideDispatchModalButtonType.TIME_DROPDOWN,
              operationId: this.operation.id,
            }),
          label: () => t('menu_olo.dispatchModal.timePicker'),
          value: () => {
            if (!this.isViewer) {
              setTimeout(
                () =>
                  this.$w(
                    DISPATCH_MODAL_WIDGET_COMPONENT_IDS.dateTimeSlots
                  ).resetValidityIndication(),
                0
              );
            }
            return this.form.selectedOption?.value;
          },
          collapsed: () =>
            (this.form.dropdownTimeSlotsOptions.length === 0 || this.store.isLoading) &&
            !this.store.isChangingLocation,
          disabled: () => this.store.isChangingLocation,
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.schedulingPicker]: {
          collapsed: () =>
            !this.form.canSubmitOrderForNow &&
            !(this.store.isChangingLocation || this.store.isLoading),
          onChange: (evt: $w.Event) => {
            biHandler.onSchedulingTypeChange(evt.target.value);
            this.form.setSchedulingType(evt.target.value);
          },
          disabled: () =>
            this.form.hasError || this.store.isLoading || this.store.isChangingLocation,
          options: () => [
            {
              value: SchedulingType.ASAP,
              label: getASAPString(this.store.asapTimeExact, this.store.asapTimeRange, t),
            },
            {
              value: SchedulingType.ASAP_AND_FUTURE,
              label: t('menu_olo.dispatch.time.Later'),
            },
          ],
          value: () => this.form.schedulingType,
          label: () => t('menu_olo.dispatchModal.delivery.when'),
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.timeMultiState]: {
          disabled: () => this.store.isChangingLocation,
          collapsed: () =>
            (this.store.isLoading ||
              this.store.showPickupLocationSelector ||
              this.form.shouldCollapseTimeMultiStateBox ||
              !dispatchState.isSelectedDispatchAvailable()) &&
            !this.store.isChangingLocation,
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.fulfillmentPicker]: {
          collapsed: () => !this.store.hasMoreThanOneDispatchType,
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.pickupAddress]: {
          collapsed: () => this.store.showPickupLocationSelector,
          text: () => this.store.pickupAddress?.formattedAddress ?? '',
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.pickupHeader]: {
          text: () => t('menu_olo.dispatchModal.pickup.from'),
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.title]: {
          text: () => t('menu_olo.dispatchModal.headerTitle'),
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.asapTime]: {
          text: () => t('menu_olo.dispatchModal.delivery.when'),
          disabled: () => this.store.isLoading || this.store.isChangingLocation,
          collapsed: () =>
            (this.form.hasError || !(this.store.asapTimeExact || this.store.asapTimeRange)) &&
            !(this.store.isChangingLocation || this.store.isLoading),
        },
        [DISPATCH_MODAL_WIDGET_COMPONENT_IDS.preOrderTime]: {
          text: () => t('menu_olo.dispatchModal.delivery.when'),
          disabled: () => this.store.isLoading || this.store.isChangingLocation,
          collapsed: () =>
            this.form.hasError ||
            (this.store.timeSlots.length === 0 &&
              !(this.store.isChangingLocation || this.store.isLoading)),
        },
      });
      // NOTE: reset validity on clear
      this.$w(DISPATCH_MODAL_WIDGET_COMPONENT_IDS.addressInput).onCustomValidation(
        (value: AddressInputAddress) => {
          if (this.form.address?.formatted && !value.formatted) {
            this.form.setAddress(value);
          }
        },
        false
      );
      this.isViewer && onModalOpen();
      this.biReporterService?.reportOloLiveSiteDispatchModalOpenedBiEvent({
        isAsapDisplayed: this.form.canSubmitOrderForNow,
        availableDispatchTypes: dispatchState.configuredDispatchTypes,
        isPreorderDisplayed: this.form.isPreOrder,
        schedulingType: this.store.schedulingType,
        currentFulfillment: this.store.dispatchType,
        operationId: this.operation.id,
        isMemberLoggedIn,
        selectedSchedulingRadioButton: this.form.isSchedulingPickerDisplayed
          ? this.form.schedulingType
          : undefined,
        selectedTime: dispatchState.dispatchInfo.selectedTimeSlot,
        businessDays: this.operation.businessDaysAheadHandlingOptions,
        operationGroup: this.operation.operationGroupId,
        locationId: this.operation.locationId,
        isDefaultLocation: this.operation.locationDetails?.default,
      });
    } catch (e) {
      this.flowAPI.reportError(e as Error);
      this.flowAPI.panoramaClient?.errorMonitor()?.reportError(e as Error);
    }
  }

  changeSchedulingTypeModalState() {
    const multiStateBox = this.$w(DISPATCH_MODAL_WIDGET_COMPONENT_IDS.timeMultiState);
    multiStateBox.changeState(this.store.schedulingTypeModalState);
  }
}
